import sanitizer from '@helpers/sanitizer';
import Icon from '@components/atoms/icon/icon';
import { BrandsInterface } from '@interfaces/models/deposit';
import { ListingContext } from '@context/listing.context';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import commonStyles from '../../dropdown.module.scss';
import styles from './index.module.scss';

interface BrandsMenuInterface {
  setCloseMenu: (close: boolean) => void;
}

const BrandsMenu: React.FC<BrandsMenuInterface> = ({ setCloseMenu }): React.JSX.Element => {
  const { brandsByFilter, brandSearchTerm, listingDraftPayload, setBrandSearchTerm, setListingDraftAttribute } =
    useContextSelector(ListingContext, (state) => state);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useTranslation('pricing-calculator');

  /**
   *
   * @param e Change event of type HTMLInputElement
   * Matches brand names to pull the selected brand id. Sets the brand id.
   */
  const handleChangeBrand = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBrandSearchTerm(e.target.value);
  };

  /**
   *
   * @param brand of type IBrandInterface
   * The function sets the brand id and the search term for autocompletion effect
   * finally, it closes the options list
   * @returns void
   */
  const handleSelectBrand = (brand: BrandsInterface) => {
    setListingDraftAttribute({ brand: brand.pimId });
  };

  const handleIconClick = () => {
    setBrandSearchTerm('');
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      <div className={styles.brandsMenu}>
        <span
          className="visuallyhidden"
          id="brandsMenuLabel"
        >
          {t('PRICING_CALCULATOR.BRANDS_MENU.LABEL')}
        </span>
        <fieldset className={styles.brandsMenu__search}>
          <i className={styles.brandsMenu__search__icon}>
            <Icon
              name="search-24"
              className={styles.brandsMenu__search__icon__search}
            />
          </i>
          <label htmlFor="pricingCalculatorSearchBrands">
            <span className="visuallyhidden">{t('PRICING_CALCULATOR.BRANDS_MENU.PLACEHOLDER')}</span>
          </label>
          <input
            ref={inputRef}
            type="text"
            placeholder={t('PRICING_CALCULATOR.BRANDS_MENU.PLACEHOLDER')}
            id="pricingCalculatorSearchBrands"
            className={clsx('vc-inputText', styles.brandsMenu__search__input)}
            onChange={handleChangeBrand}
            value={brandSearchTerm}
          />
          <i
            className={styles.brandsMenu__search__icon}
            onClick={handleIconClick}
            onKeyDown={handleIconClick}
            role="button"
            tabIndex={0}
          >
            <Icon
              name="close-16"
              className={styles.brandsMenu__search__icon__close}
            />
          </i>
        </fieldset>
        <ul
          className={clsx(commonStyles.dropdownMenu, styles.brandsMenu__list)}
          role="menu"
          tabIndex={-1}
        >
          {brandsByFilter.length > 0 ? (
            brandsByFilter.map((brand, index) => (
              <li
                tabIndex={index + 1}
                role="menuitem"
                key={brand.pimId}
                className={clsx(commonStyles.dropdownMenu__option, commonStyles['dropdownMenu__option--brand'], {
                  [commonStyles.selected]: brand.pimId === listingDraftPayload.brand,
                })}
                onClick={() => {
                  handleSelectBrand(brand);
                  setCloseMenu(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSelectBrand(brand);
                    setCloseMenu(true);
                  }
                }}
              >
                {brand.name}
              </li>
            ))
          ) : (
            <li
              key={'brand_not_found'}
              role="menuitem"
              className={clsx(commonStyles.dropdownMenu__option, styles.brandsMenu__search__notFound)}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(t('PRICING_CALCULATOR.BRANDS_MENU.NOT_FOUND')),
                }}
              />
            </li>
          )}
        </ul>
      </div>
    </>
  );
};

export default BrandsMenu;
